import { Grid, Tooltip as MuiTooltip, IconButton, Typography } from "@mui/material";
import {
  AreaChart as RechartsArea,
  Tooltip,
  XAxis,
  Area,
  ResponsiveContainer,
  CartesianGrid,
  YAxis,
} from "recharts";
import { CustomText } from "src/design/CustomText";
import { TrendChip } from "src/design/TrendChip";
import { chartsPalette } from "src/design/ColorPalette";
import InfoIcon from "@mui/icons-material/Info";
import NoDataFound from "src/assets/no-data-found.png";
import { colorBorder } from "./tokens";

interface Props {
  data: Record<string, string | number>[];
  title?: string;
  subtitle?: string;
  value?: string;
  progression?: number;
  palette?: string[];
  dataKeys: string[];
  tooltip?: string;
  compact?: boolean;
  xAxisTickFormatter?: (value: string) => string;
  xAxisInterval?: number | "preserveStart" | "preserveEnd" | "preserveStartEnd";
  xAxisAngle?: number;
}

export const AreaChart = ({
  data,
  title,
  subtitle,
  value,
  progression,
  palette,
  dataKeys,
  tooltip,
  compact,
  xAxisTickFormatter,
  xAxisInterval,
  xAxisAngle,
}: Props) => {
  const colorPalette = palette || chartsPalette[`${dataKeys.length}`];

  return (
    <Grid container width={"100%"} height={"100%"}>
      {(title || subtitle || (value && data.length > 0)) && (<Grid item textAlign={"left"} width={"100%"}>
        {title && (
          <Grid container alignItems={"center"} width={"100%"}>
            <Grid item>
              <CustomText component={"h3"} type="subheader" label={title} />
            </Grid>
            {tooltip && (
              <MuiTooltip title={tooltip} sx={{ padding: 0, marginLeft: 0.5 }}>
                <IconButton>
                  <InfoIcon fontSize="small" />
                </IconButton>
              </MuiTooltip>
            )}
          </Grid>
        )}
        {subtitle && (
          <Typography sx={{ fontSize: "0.75rem", color: "#6B7280", mb: 0.5 }}>
            {subtitle}
          </Typography>
        )}
        {value && data.length > 0 && (
          <Grid
            container
            direction={"row"}
            justifyContent={"flex-start"}
            spacing={1}
            alignItems={"center"}
          >
            <Grid item>
              <CustomText
                component={"h4"}
                type="section"
                label={
                  Number(value) > 100000 ? `${Number(value) / 100}k` : value
                }
                fontFamily="sans-serif"
              />
            </Grid>
            {progression && (
              <Grid item>
                <TrendChip progress={progression} />
              </Grid>
            )}
          </Grid>
        )}
      </Grid>)}
      {data.length === 0 ? (
        <Grid container justifyContent={"center"}>
          <img src={NoDataFound} alt="No data found" />
        </Grid>
      ) : (
        <ResponsiveContainer style={{ marginLeft: 10 }} width={"100%"} height={250}>
          <RechartsArea height={350} data={data}>
            <CartesianGrid stroke={colorBorder} strokeDasharray="5 5" />
            <defs>
              {dataKeys.map((key, index) => (
                <linearGradient
                  key={index}
                  id={key + "-" + index}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={colorPalette[index]}
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor={colorPalette[index]}
                    stopOpacity={0}
                  />
                </linearGradient>
              ))}
            </defs>
            <YAxis />
            <XAxis
              dataKey={"name"}
              tickFormatter={xAxisTickFormatter}
              interval={xAxisInterval}
              angle={xAxisAngle}
              textAnchor={xAxisAngle ? "end" : "middle"}
              height={xAxisAngle ? 50 : undefined}
            />
            <Tooltip />
            {dataKeys.map((key, index) => (
              <Area
                key={index}
                type="monotone"
                dataKey={key}
                stroke={colorPalette[index]}
                fillOpacity={1}
                fill={`url(#${key + "-" + index})`}
              />
            ))}
          </RechartsArea>
        </ResponsiveContainer>
      )}
    </Grid>
  );
};
